// Bridges Potree clip state (scene.volumes with clip=true + viewer.clipTask
// + SphereVolume gradient tint + profile.boxes) to spark's SplatEdit / dyno
// modifier API so 3DGS scenes clip/highlight consistently with the point-cloud
// path.
//
// Wiring:
//   1. PotreeSparkAdapter calls install(viewer, sparkNS) right after the
//      first splat is loaded.
//   2. viewer.js calls viewer._updateSplatClip() at the tail of its clip
//      distribution loop each frame.
//
// Splat pipeline stages we attach to (spark.module.js:12444-12500):
//
//   • Box / Profile clip / HIGHLIGHT tint → SplatEdit + SplatEditSdf(box).
//     Applied by applyGsplatRgbaDisplaceEdits BEFORE worldModifiers.
//     Respects viewer.clipTask (HIGHLIGHT / SHOW_INSIDE / SHOW_OUTSIDE).
//     Box half-extent = matrixWorld.scale / 2.
//
//   • SphereVolume SPECTRAL gradient  → SplatMesh.worldModifiers (dyno block).
//     Applied AFTER SplatEdit stage, so sphere gradient overlays the box tint
//     inside the sphere and leaves everything outside untouched — same order
//     as pointcloud.vs where uClipSpheres runs after the isSelected/highlight
//     block. Per-splat samples the SPECTRAL gradient texture with UV
//     (w, 1-w), w = radial distance in the sphere's local (ellipsoid) frame,
//     matching the point-cloud shader exactly (pointcloud.vs:1043-1057).
//
// HIGHLIGHT box tint uses add_rgba with sdfRgba = (0.5, 0, 0, 0), which the
// spark blend gives as `rgba + (0.5, 0, 0, 0)` — pixel-equivalent to the
// point-cloud shader's `vColor.r += 0.5` (pointcloud.vs:1036-1038).
//
//   • YDPolygonVolume 多边形棱柱 → 单个 dyno worldModifier（所有棱柱合一）。
//     even-odd 判定与 pointcloud.vs 的 pointInClipPrism 同一公式（循环展开，
//     顶点作为编译期常量内嵌，顶点数变化才重建表达式）；棱柱位姿/缩放/zMin/zMax
//     与 clipTask 语义全部走 uniform 就地更新。多棱柱用 max 合并 = INSIDE_ANY。
//
// Limitations:
//   - PolygonClipVolume is ignored on the GS side (spark has no NDC
//     polygon SDF); PC keeps working unchanged. YDPolygonVolume 多边形棱柱
//     已通过 dyno worldModifier 路径镜像（见上）。
//   - ClipMethod.INSIDE_ALL degrades to INSIDE_ANY on GS (spark's SDF
//     combine is smooth-min = union, not intersection)。多边形棱柱同理：
//     max 合并语义即 INSIDE_ANY。

import * as THREE from "../../../libs/three.js/build/three.module.js";
import { ClipTask } from "../../defines.js";
import { SphereVolume } from "../../utils/Volume.js";
import { YDPolygonVolume } from "../../utils/YDPolygonVolume.js";
import { Gradients } from "../../materials/Gradients.js";

// Reusable scratch — spark treats sdf.scale.xyz as the box HALF-extent, but
// Potree BoxVolume uses BoxGeometry(1,1,1) × volume.scale (FULL width) so
// halve for box.
const _tmpScale = new THREE.Vector3();
const _tmpQuat  = new THREE.Quaternion();
const _tmpQuatInv = new THREE.Quaternion();
const _tmpPos   = new THREE.Vector3();
const _tmpInvRadii = new THREE.Vector3();

// Public: called once after spark is loaded and at least one splat is on scene.
export function install(viewer, sparkNS) {
	// 自愈：清除高斯(removeAllGaussianSplats)会把 editBox(SplatEdit)从
	// sceneGaussianSplat 移除并 dispose，但调用方未必清理 viewer._splatClipBridge
	// / _splatClipInstalled(如 demo 页手动清除只删了 __sparkRenderer)。重新加载后
	// 若复用这个 editBox 已失效的旧 bridge，_ensureEditBox 因 this.editBox 非空跳过
	// 重建 -> 裁剪/高亮失效(不红染)。检测 editBox 已脱离场景(parent===null)即销毁
	// 旧 bridge 重建，使 install 对任意清除路径都健壮。
	const old = viewer._splatClipBridge;
	if (old && old.editBox && old.editBox.parent === null) {
		old.dispose();
		viewer._splatClipBridge = null;
	}
	if (!viewer._splatClipBridge) {
		viewer._splatClipBridge = new SplatClipBridge(viewer, sparkNS);
	}
	viewer._updateSplatClip = () => viewer._splatClipBridge.update();
}

// Build the SPECTRAL gradient texture the same way PointCloudMaterial does —
// diagonal linear-gradient on a 64×64 canvas so texture(gradient, vec2(w,1-w))
// samples t = w on the diagonal axis (see PointCloudMaterial.generateGradientTexture).
// Isolated copy here to avoid coupling with PointCloudMaterial's caching.
let _sharedGradientTexture = null;
function getSpectralGradientTexture() {
	if (_sharedGradientTexture) return _sharedGradientTexture;
	const size = 64;
	const canvas = document.createElement("canvas");
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext("2d");
	const grad = ctx.createLinearGradient(0, 0, size, size);
	for (const stop of Gradients.SPECTRAL) {
		grad.addColorStop(stop[0], "#" + stop[1].getHexString());
	}
	ctx.fillStyle = grad;
	ctx.fillRect(0, 0, size, size);
	const tex = new THREE.CanvasTexture(canvas);
	tex.minFilter = THREE.LinearFilter;
	tex.magFilter = THREE.LinearFilter;
	tex.wrapS = THREE.ClampToEdgeWrapping;
	tex.wrapT = THREE.ClampToEdgeWrapping;
	tex.needsUpdate = true;
	_sharedGradientTexture = tex;
	return tex;
}

class SplatClipBridge {
	constructor(viewer, sparkNS) {
		this.viewer   = viewer;
		this.sparkNS  = sparkNS;
		this.editBox  = null;
		this.sdfsBox  = [];

		// 设备区域着色(与 clip 独立)：一个 SplatEdit(set_rgb) + 每盒一个 SDF。
		this.editColor = null;
		this.sdfsColor = [];

		// Per-SphereVolume dyno modifier entries.
		// key: SphereVolume  val: { modifier, uCenter, uQuatInv, uInvRadii, uEnabled }
		this._sphereModifiers = new Map();
		this._lastSpheresKey  = "";
		this._lastMeshesKey   = "";

		// YDPolygonVolume 多边形棱柱：所有棱柱合进单个 dyno modifier（alpha 因子
		// 要跨棱柱用 max 合并，拆开各自乘会退化成交集语义）。形状签名（uuid:顶点数）
		// 变化才重建；位姿/zRange/clipTask 走 uniform 就地更新。
		this._prismEntry   = null;  // { modifier, uEnabled, uKeepInside, uAlphaClip, uTint, prisms: [...] }
		this._lastPrismsKey = "";
		this._lastPrismMeshesKey = "";

		// 拖动期降频：拖动 volume 时 box matrix 每帧变 -> 每帧写 SDF -> spark
		// 全量重生成+重排序(很重)。拖动期每 _dragThrottleInterval ms 才写一次，
		// drop 后立即恢复全速(drag 变 null 时 _dragThrottleLast 重置为 0)。
		this._dragThrottleLast    = 0;
		this._dragThrottleInterval = 100;
	}

	// 显式释放：清空 sphere dyno modifier(uEnabled 置 0)并丢弃对 editBox/sdfsBox
	// 的引用。editBox(SplatEdit)本身由 Scene.removeAllGaussianSplats 的 children
	// 循环 dispose+remove，这里只清 bridge 自身持有的引用，避免重新加载后复用已
	// dispose 的 editBox 导致裁剪/高亮失效(不红染)。
	dispose() {
		for (const entry of this._sphereModifiers.values()) {
			try { entry.uEnabled.value = 0; } catch (e) { /* noop */ }
		}
		this._sphereModifiers.clear();
		this._lastSpheresKey = "";
		this._lastMeshesKey  = "";
		if (this._prismEntry) {
			try { this._prismEntry.uEnabled.value = 0; } catch (e) { /* noop */ }
		}
		this._prismEntry = null;
		this._lastPrismsKey = "";
		this._lastPrismMeshesKey = "";
		this.sdfsBox = [];
		this.editBox = null;
		this.sdfsColor = [];
		this.editColor = null;
	}

	_ensureEditBox() {
		if (this.editBox) return;
		const { SplatEdit } = this.sparkNS;
		this.editBox = new SplatEdit({
			rgbaBlendMode: "multiply",
			sdfSmooth: 0,
			softEdge: 0
		});
		this.editBox.ordering = 100;
		this.viewer.scene.sceneGaussianSplat.add(this.editBox);
	}

	// 设备区域着色专用 SplatEdit：set_rgb 直接把盒内 splat 的 rgb 设为盒色。
	// ordering 101 > 裁剪高亮 100，让着色覆盖在裁剪高亮之上。
	_ensureEditColor() {
		if (this.editColor) return;
		const { SplatEdit } = this.sparkNS;
		this.editColor = new SplatEdit({
			rgbaBlendMode: "set_rgb",
			sdfSmooth: 0,
			softEdge: 0
		});
		this.editColor.ordering = 101;
		this.viewer.scene.sceneGaussianSplat.add(this.editColor);
	}

	_resizeSdfs(list, edit, n, sdfType) {
		const { SplatEditSdf } = this.sparkNS;
		while (list.length < n) {
			const sdf = new SplatEditSdf({ type: sdfType, opacity: 0 });
			edit.add(sdf);
			list.push(sdf);
		}
		while (list.length > n) {
			const sdf = list.pop();
			edit.remove(sdf);
		}
	}

	// 设备区域着色 —— 由 DeviceRegionManager 每帧转发调用(独立于 clip 的 update)。
	// entries: [{ matrixWorld:Matrix4, color:THREE.Color, intensity:number }]
	// 盒内 splat 的 rgb 直接设为 color*intensity(闪烁时 intensity 振荡 → 颜色脉动)。
	setColorBoxes(entries) {
		const scene = this.viewer.scene;
		const hasSplats = scene.gaussianSplats && scene.gaussianSplats.length > 0;

		if (!hasSplats || !entries || entries.length === 0) {
			if (this.editColor) this.editColor.visible = false;
			return;
		}

		this._ensureEditColor();
		this._resizeSdfs(this.sdfsColor, this.editColor, entries.length, "box");
		this.editColor.visible = true;

		for (let i = 0; i < entries.length; i++) {
			const e = entries[i];
			const sdf = this.sdfsColor[i];
			// matrixWorld 携带盒的完整宽度；spark SDF 的 scale 是半宽，×0.5。
			e.matrixWorld.decompose(sdf.position, sdf.quaternion, _tmpScale);
			sdf.scale.copy(_tmpScale).multiplyScalar(0.5);
			sdf.invert  = false;
			// set_rgb 合成: target.a = 原alpha * sdf.opacity。必须为 1.0，
			// 否则盒内 splat 的 alpha 被乘成 0 → 完全透明(点消失，看似"不显色")。
			sdf.opacity = 1.0;
			if (sdf.color) {
				sdf.color.setRGB(
					e.color.r * e.intensity,
					e.color.g * e.intensity,
					e.color.b * e.intensity
				);
			}
			sdf.updateMatrixWorld();
		}
	}

	update() {
		const scene = this.viewer.scene;
		const hasSplats = scene.gaussianSplats && scene.gaussianSplats.length > 0;
		if (!hasSplats) {
			if (this.editBox) this.editBox.visible = false;
			// Deactivate sphere modifiers on all splat meshes we know about.
			for (const entry of this._sphereModifiers.values()) {
				entry.uEnabled.value = 0;
			}
			// 棱柱 modifier 同步休眠
			if (this._prismEntry) {
				this._prismEntry.uEnabled.value = 0;
			}
			return;
		}

		// 拖动期降频：避免每帧写 SDF 触发 spark 全量重生成+重排序。
		// 非拖动(含 drop 后首帧)全速更新，保证静态/松手后高亮即时。
		const dragging = !!(this.viewer.inputHandler && this.viewer.inputHandler.drag);
		if (dragging) {
			const now = performance.now();
			if (now - this._dragThrottleLast < this._dragThrottleInterval) {
				return;
			}
			this._dragThrottleLast = now;
		} else {
			this._dragThrottleLast = 0;
		}

		this._updateBoxes(scene);
		this._updateSpheres(scene);
		this._updatePrisms(scene);
	}

	// Box path — mirrors viewer.js clipBoxes distribution (volumes.clip=true +
	// profile.boxes). Respects viewer.clipTask, gated same as PC materials.
	_updateBoxes(scene) {
		// Exclude SphereVolume — SphereVolume has its own dyno modifier path;
		// treating it as a clip box would produce a bounding-cube edit.
		// YDPolygonVolume 同理走 _updatePrisms 的 dyno 路径，不能当盒 SDF。
		const boxes = scene.volumes.filter(
			v => v && v.clip === true && !(v instanceof SphereVolume) && !(v instanceof YDPolygonVolume)
		);

		// profile.box is THREE.Mesh(BoxGeometry(1,1,1)) with box.scale = full
		// width, same convention as BoxVolume — safe to reuse ×0.5 logic.
		if (scene.profiles && scene.profiles.length > 0) {
			for (const profile of scene.profiles) {
				if (profile && profile.boxes) boxes.push(...profile.boxes);
			}
		}

		const task = this.viewer.clipTask;

		if (boxes.length === 0 || task === ClipTask.NONE || task == null) {
			if (this.editBox) this.editBox.visible = false;
			return;
		}

		this._ensureEditBox();
		this._resizeSdfs(this.sdfsBox, this.editBox, boxes.length, "box");
		this.editBox.visible = true;

		let invert, opacity, blend, tint = null;
		switch (task) {
			case ClipTask.SHOW_INSIDE:
				invert = true;  opacity = 0; blend = "multiply"; break;
			case ClipTask.SHOW_OUTSIDE:
				invert = false; opacity = 0; blend = "multiply"; break;
			case ClipTask.HIGHLIGHT:
				// Match pointcloud.vs `vColor.r += 0.5` exactly:
				// add_rgba blend + sdfRgba = (0.5, 0, 0, 0)  →  target = rgba + (0.5,0,0,0).
				// sdf.opacity = 0 keeps the alpha channel unchanged (a += 0).
				invert = false; opacity = 0; blend = "add_rgba";
				tint = { r: 0.5, g: 0, b: 0 };
				break;
			default:
				this.editBox.visible = false;
				return;
		}
		this.editBox.rgbaBlendMode = blend;

		for (let i = 0; i < boxes.length; i++) {
			const box = boxes[i];
			const sdf = this.sdfsBox[i];
			box.updateMatrixWorld();
			// Decompose into a temporary — sdf.scale is spark's half-extent, while
			// BoxVolume.matrixWorld carries the full width, so halve it.
			box.matrixWorld.decompose(sdf.position, sdf.quaternion, _tmpScale);
			sdf.scale.copy(_tmpScale).multiplyScalar(0.5);
			sdf.invert  = invert;
			sdf.opacity = opacity;
			if (tint && sdf.color) sdf.color.setRGB(tint.r, tint.g, tint.b);
			sdf.updateMatrixWorld();
		}
	}

	// Sphere path — mirrors pointcloud.vs uClipSpheres SPECTRAL gradient
	// (always on, no clipTask gating). Uses a dyno worldModifier per volume:
	// each modifier tints splats whose center falls inside its ellipsoid.
	_updateSpheres(scene) {
		const spheres = scene.volumes.filter(v => v instanceof SphereVolume);
		const meshes  = scene.gaussianSplats || [];

		// Detect set changes (volumes added/removed OR splat meshes added/removed).
		const spheresKey = spheres.map(v => v.uuid).join("|");
		const meshesKey  = meshes.map(m => m.uuid).join("|");
		const changed = (spheresKey !== this._lastSpheresKey)
			|| (meshesKey !== this._lastMeshesKey);

		if (changed) {
			this._rebuildSphereModifiers(spheres, meshes);
			this._lastSpheresKey = spheresKey;
			this._lastMeshesKey  = meshesKey;
		}

		// Frame update — write uniforms, and detect matrixWorld deltas so we
		// can flag the SplatMesh dirty. Without this the dyno uniform value
		// changes silently: spark's SplatMesh.update() (spark.module.js:12701)
		// only calls updateVersion() when its own `updated` flag flips, and
		// worldModifier uniform writes don't hit that path. Result would be a
		// stale accumulator during volume drag — colors "jump" at drag start
		// and end but stay frozen while dragging (which is exactly what user
		// observed). Marking the mesh needsUpdate whenever any sphere's
		// matrix changed forces the accumulator to recompute that frame.
		let anyMatrixChanged = changed;
		for (const vol of spheres) {
			const entry = this._sphereModifiers.get(vol);
			if (!entry) continue;
			vol.updateMatrixWorld();
			if (!entry.lastMatrix) entry.lastMatrix = new THREE.Matrix4();
			if (!entry.lastMatrix.equals(vol.matrixWorld)) {
				anyMatrixChanged = true;
				entry.lastMatrix.copy(vol.matrixWorld);
			}
			vol.matrixWorld.decompose(_tmpPos, _tmpQuat, _tmpScale);
			_tmpQuatInv.copy(_tmpQuat).invert();
			// Per-axis inverse radii (guard against zero scale to avoid NaN in shader).
			const rx = Math.abs(_tmpScale.x) > 1e-6 ? _tmpScale.x : 1e-6;
			const ry = Math.abs(_tmpScale.y) > 1e-6 ? _tmpScale.y : 1e-6;
			const rz = Math.abs(_tmpScale.z) > 1e-6 ? _tmpScale.z : 1e-6;
			_tmpInvRadii.set(1 / rx, 1 / ry, 1 / rz);

			entry.uCenter.value.copy(_tmpPos);
			// Store quaternion as vec4 (x, y, z, w).
			entry.uQuatInv.value.set(_tmpQuatInv.x, _tmpQuatInv.y, _tmpQuatInv.z, _tmpQuatInv.w);
			entry.uInvRadii.value.copy(_tmpInvRadii);
			if (entry.uEnabled.value !== 1) {
				entry.uEnabled.value = 1;
				anyMatrixChanged = true;
			}
		}

		if (anyMatrixChanged) {
			for (const mesh of meshes) {
				mesh.needsUpdate = true;
			}
		}
	}

	_rebuildSphereModifiers(spheres, meshes) {
		// Drop entries for volumes that no longer exist.
		for (const vol of Array.from(this._sphereModifiers.keys())) {
			if (!spheres.includes(vol)) this._sphereModifiers.delete(vol);
		}
		// Create missing entries for newly-added volumes.
		for (const vol of spheres) {
			if (!this._sphereModifiers.has(vol)) {
				this._sphereModifiers.set(vol, this._makeSphereModifier());
			}
		}

		this._applyWorldModifiers(meshes);
	}

	// Compose the full worldModifiers array (sphere gradient modifiers + prism
	// clip modifier, order irrelevant — each only writes its own region) and
	// attach to every splat mesh with a generator rebuild. Only paid on
	// set/shape changes (low-frequency events).
	_applyWorldModifiers(meshes) {
		const modArr = Array.from(this._sphereModifiers.values()).map(e => e.modifier);
		if (this._prismEntry) {
			modArr.push(this._prismEntry.modifier);
		}
		for (const mesh of meshes) {
			mesh.worldModifiers = modArr.length > 0 ? modArr : void 0;
			if (typeof mesh.updateGenerator === "function") {
				mesh.updateGenerator();
			}
		}
	}

	// Build a single dyno block that reads a splat's world-space center,
	// transforms it into the sphere's local ellipsoid frame, and if the
	// resulting radial distance is < 1 replaces the splat's rgb with a
	// sample from the shared SPECTRAL gradient texture at UV = (w, 1-w).
	//
	// Uses only lowercase dyno helpers exposed at spark.module.js:6493-6939.
	// GLSL equivalent (informational):
	//
	//   vec3 offset  = center - uCenter;
	//   vec3 local   = quatVec(uQuatInv, offset) * uInvRadii;
	//   float d      = length(local);
	//   float inside = step(d, 1.0) * uEnabled;
	//   float w      = clamp(d, 0.0, 1.0);
	//   vec3 grad    = texture(uGradient, vec2(w, 1.0 - w)).xyz;
	//   rgb          = mix(rgb, grad, inside);
	_makeSphereModifier() {
		const dyno = this.sparkNS.dyno;
		const {
			dynoBlock, dynoVec3, dynoVec4, dynoFloat, dynoSampler2D, dynoConst,
			Gsplat, splitGsplat, combineGsplat,
			transformDir, sub, mul, length, clamp, step, mix, texture, combine, swizzle,
		} = dyno;

		const uCenter   = dynoVec3(new THREE.Vector3());
		const uQuatInv  = dynoVec4(new THREE.Vector4(0, 0, 0, 1));
		const uInvRadii = dynoVec3(new THREE.Vector3(1, 1, 1));
		const uEnabled  = dynoFloat(0);
		const uGradient = dynoSampler2D(getSpectralGradientTexture());

		const modifier = dynoBlock(
			{ gsplat: Gsplat }, { gsplat: Gsplat },
			({ gsplat }) => {
				const { center, rgb: curRgb } = splitGsplat(gsplat).outputs;

				// Transform world center into the sphere's local ellipsoid frame:
				// offset = center - uCenter; local = quatVec(uQuatInv, offset) * uInvRadii
				const offset  = sub(center, uCenter);
				const rotated = transformDir(offset, { rotate: uQuatInv });
				const local   = mul(rotated, uInvRadii);

				const d = length(local);
				const insideMask = step(d, dynoConst("float", 1));
				const inside     = mul(insideMask, uEnabled);

				const w        = clamp(d, dynoConst("float", 0), dynoConst("float", 1));
				const oneMinus = sub(dynoConst("float", 1), w);
				const uv       = combine({ vectorType: "vec2", x: w, y: oneMinus });
				const gradRgba = texture(uGradient, uv);
				const gradRgb  = swizzle(gradRgba, "xyz");

				// mix(cur_rgb, gradient_rgb, inside) — 0 outside sphere, 1 inside.
				const newRgb = mix(curRgb, gradRgb, inside);

				return { gsplat: combineGsplat({ gsplat, rgb: newRgb }) };
			}
		);

		return { modifier, uCenter, uQuatInv, uInvRadii, uEnabled };
	}

	// ─────────────────────────────────────────────────────────────────────
	// YDPolygonVolume 多边形棱柱路径（dyno worldModifier，所有棱柱合一）
	// ─────────────────────────────────────────────────────────────────────

	// Mirror of pointcloud.vs pointInClipPrism + viewer.js clipPrisms collection:
	// clip=true 的 YDPolygonVolume，按 viewer.clipTask 三态裁剪/高亮 splat。
	// INSIDE_ALL 在此退化为 INSIDE_ANY（max 合并，见文件头 Limitations）。
	_updatePrisms(scene) {
		const prisms = scene.volumes.filter(v =>
			v && v.clip === true && v instanceof YDPolygonVolume
			&& v.points && v.points.length >= 3);
		const meshes = scene.gaussianSplats || [];
		const task = this.viewer.clipTask;

		if (prisms.length === 0 || task === ClipTask.NONE || task == null) {
			// 关闸必须标脏：uEnabled 翻转后要触发 spark 重建 accumulator，否则画面
			// 停在上一帧红染（needsUpdate 语义见下方 anyChanged 注释）
			if (this._prismEntry && this._prismEntry.uEnabled.value !== 0) {
				this._prismEntry.uEnabled.value = 0;
				for (const mesh of meshes) mesh.needsUpdate = true;
			}
			return;
		}

		// 形状签名：uuid:顶点数。顶点坐标只在采点期变化（闭合后无再编辑入口，
		// zMin/zMax 走 uniform），所以数量足以判定重建时机。
		const key = prisms.map(v => `${v.uuid}:${v.points.length}`).join("|");
		const meshesKey = meshes.map(m => m.uuid).join("|");
		if (key !== this._lastPrismsKey || meshesKey !== this._lastPrismMeshesKey) {
			this._rebuildPrismModifier(prisms, meshes);
			this._lastPrismsKey = key;
			this._lastPrismMeshesKey = meshesKey;
		}

		const entry = this._prismEntry;
		let anyChanged = false;
		for (let i = 0; i < prisms.length; i++) {
			const vol = prisms[i];
			const e = entry.prisms[i];
			vol.updateMatrixWorld();
			if (!e.lastMatrix) e.lastMatrix = new THREE.Matrix4();
			if (!e.lastMatrix.equals(vol.matrixWorld)) {
				anyChanged = true;
				e.lastMatrix.copy(vol.matrixWorld);
			}
			// 局部 = R⁻¹(center - pos) ⊘ scale —— 与逆 TRS 矩阵等价，sphere 路径同款。
			vol.matrixWorld.decompose(_tmpPos, _tmpQuat, _tmpScale);
			_tmpQuatInv.copy(_tmpQuat).invert();
			const sx = Math.abs(_tmpScale.x) > 1e-6 ? _tmpScale.x : 1e-6;
			const sy = Math.abs(_tmpScale.y) > 1e-6 ? _tmpScale.y : 1e-6;
			const sz = Math.abs(_tmpScale.z) > 1e-6 ? _tmpScale.z : 1e-6;

			e.uPos.value.copy(_tmpPos);
			e.uQuatInv.value.set(_tmpQuatInv.x, _tmpQuatInv.y, _tmpQuatInv.z, _tmpQuatInv.w);
			e.uInvScale.value.set(1 / sx, 1 / sy, 1 / sz);
			e.uZRange.value.set(vol.zMin, vol.zMax);
		}

		// clipTask 语义 uniforms（与 _updateBoxes 的 SDF 映射同语义）：
		// SHOW_INSIDE  → alpha ×= any（盒内保留）
		// SHOW_OUTSIDE → alpha ×= (1-any)（盒外保留）
		// HIGHLIGHT    → alpha 不动，盒内 rgb += (0.5,0,0)
		switch (task) {
			case ClipTask.SHOW_INSIDE:
				entry.uKeepInside.value = 1; entry.uAlphaClip.value = 1; entry.uTint.value = 0;
				break;
			case ClipTask.SHOW_OUTSIDE:
				entry.uKeepInside.value = 0; entry.uAlphaClip.value = 1; entry.uTint.value = 0;
				break;
			case ClipTask.HIGHLIGHT:
				entry.uKeepInside.value = 1; entry.uAlphaClip.value = 0; entry.uTint.value = 1;
				break;
			default:
				if (entry.uEnabled.value !== 0) {
					entry.uEnabled.value = 0;
					for (const mesh of meshes) mesh.needsUpdate = true;
				}
				return;
		}
		if (entry.uEnabled.value !== 1) {
			entry.uEnabled.value = 1;
			anyChanged = true;
		}

		// sphere 路径同款：uniform 变了要标记 mesh needsUpdate，否则拖动期颜色冻结
		if (anyChanged) {
			for (const mesh of meshes) {
				mesh.needsUpdate = true;
			}
		}
	}

	_rebuildPrismModifier(prisms, meshes) {
		const specs = prisms.map(v => ({
			// 局部顶点 XY（编译期常量内嵌进 dyno 表达式）
			verts: v.points.map(p => [p.x, p.y]),
		}));
		this._prismEntry = this._makePrismModifier(specs);
		this._applyWorldModifiers(meshes);
	}

	// Build ONE dyno block covering all prisms: even-odd per prism (unrolled,
	// vertices are compile-time constants), max-merge across prisms = INSIDE_ANY,
	// then a single alpha/tint application. Keeping all prisms in one block is
	// required for correct alpha composition (per-prism modifiers would multiply
	// alpha and degrade SHOW_INSIDE from ANY to ALL intersection semantics).
	//
	// GLSL equivalent (informational), per prism i:
	//   vec3 local = quatVec(uQuatInv_i, center - uPos_i) * uInvScale_i;
	//   float sum = 0;
	//   for each edge (vi, vj):
	//     neq  = abs(step(py, viy) - step(py, vjy));
	//     xC   = (vjx - vix) * (py - viy) / (vjy - viy) + vix;
	//     sum += neq * step(px, xC);
	//   float inXY = mod(sum, 2.0);
	//   float inZ  = step(uZMin_i, pz) * step(pz, uZMax_i);
	//   any  = max(any, inXY * inZ);
	//   alphaFactor = mix(1.0, mix(1.0 - any, any, uKeepInside), uAlphaClip * uEnabled);
	//   rgba = vec4(rgb + vec3(0.5,0,0)*any*uTint*uEnabled, rgba.a * alphaFactor);
	// uEnabled 必须进表达式（sphere 路径 inside*inside*uEnabled 同款）：否则关闸
	// （棱柱清空/ClipTask NONE）只改 uniform 无视觉效果——modifier 仍持旧棱柱 specs、
	// uTint 停在 1，红染永久残留（mirror 编辑器删漫游区实测）。
	_makePrismModifier(prismSpecs) {
		const dyno = this.sparkNS.dyno;
		const {
			dynoBlock, dynoVec3, dynoVec4, dynoVec2, dynoFloat, dynoConst,
			Gsplat, splitGsplat, combineGsplat,
			transformDir, sub, mul, div, add, step, mix, mod, abs, max, combine, swizzle,
		} = dyno;

		// 全局任务语义 uniforms —— 每帧按 viewer.clipTask 就地更新，零重建
		const uEnabled    = dynoFloat(0);
		const uKeepInside = dynoFloat(1);
		const uAlphaClip  = dynoFloat(0);
		const uTint       = dynoFloat(0);

		const prisms = prismSpecs.map(spec => ({
			uPos:      dynoVec3(new THREE.Vector3()),
			uQuatInv:  dynoVec4(new THREE.Vector4(0, 0, 0, 1)),
			uInvScale: dynoVec3(new THREE.Vector3(1, 1, 1)),
			uZRange:   dynoVec2(new THREE.Vector2(0, 0)),
			verts:     spec.verts,
			lastMatrix: null,
		}));

		const modifier = dynoBlock(
			{ gsplat: Gsplat }, { gsplat: Gsplat },
			({ gsplat }) => {
				const { center, rgba: curRgba } = splitGsplat(gsplat).outputs;
				const px0 = swizzle(curRgba, "x");
				const pg  = swizzle(curRgba, "y");
				const pb  = swizzle(curRgba, "z");
				const pa  = swizzle(curRgba, "w");

				// 所有棱柱 even-odd 判定的并（max 链 = INSIDE_ANY）
				let any = dynoConst("float", 0);
				for (const e of prisms) {
					const offset  = sub(center, e.uPos);
					const rotated = transformDir(offset, { rotate: e.uQuatInv });
					const local   = mul(rotated, e.uInvScale);
					const px = swizzle(local, "x");
					const py = swizzle(local, "y");
					const pz = swizzle(local, "z");

					// XY even-odd，与 pointcloud.vs pointInClipPrism 同一公式
					let sum = dynoConst("float", 0);
					const n = e.verts.length;
					for (let i = 0, j = n - 1; i < n; j = i++) {
						const vi = e.verts[i];
						const vj = e.verts[j];
						const aY  = step(py, dynoConst("float", vi[1]));
						const bY  = step(py, dynoConst("float", vj[1]));
						const neq = abs(sub(aY, bY));
						const xCross = add(
							div(
								mul(sub(dynoConst("float", vj[0]), dynoConst("float", vi[0])),
									sub(py, dynoConst("float", vi[1]))),
								sub(dynoConst("float", vj[1]), dynoConst("float", vi[1]))
							),
							dynoConst("float", vi[0])
						);
						const cross = step(px, xCross);
						sum = add(sum, mul(neq, cross));
					}
					const parity = mod(sum, dynoConst("float", 2));
					const zTest = mul(
						step(swizzle(e.uZRange, "x"), pz),
						step(pz, swizzle(e.uZRange, "y"))
					);
					any = max(any, mul(parity, zTest));
				}

				const alphaKeep = mix(
					sub(dynoConst("float", 1), any), any, uKeepInside);
				const alphaFactor = mix(
					dynoConst("float", 1), alphaKeep, mul(uAlphaClip, uEnabled));

				const tintR = mul(any, mul(uTint, uEnabled));
				const newRgba = combine({
					vectorType: "vec4",
					x: add(px0, tintR),
					y: pg,
					z: pb,
					w: mul(pa, alphaFactor),
				});

				return { gsplat: combineGsplat({ gsplat, rgba: newRgba }) };
			}
		);

		return { modifier, uEnabled, uKeepInside, uAlphaClip, uTint, prisms };
	}
}
